import type { Types } from 'mongoose';
import type { TLLMBlockSelection } from './upload-documents.dto';

export enum WebScraperJobStatus {
  Queued = 'queued',
  Scraping = 'scraping',
  // The crawl has finished and the backend is building and uploading the Excel
  // file. It is a distinct non-terminal state so the frontend keeps the progress
  // modal alive during this phase instead of timing out as unresponsive.
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
  Started = 'started',
  Initializing = 'initializing',
}

export interface WebScraperProgressData {
  jobId: string;
  status: WebScraperJobStatus;
  progress: number;
  total: number;
  url: string;
  exclusions?: string[];
}

export interface WebScraperCompletedData {
  jobId: string;
  dataSource: {
    _id?: Types.ObjectId;
    name: string;
    key: string;
    fileSize: number;
    fileType: string;
    documentType: string;
    trainingStatus: string;
    canBeDownloaded: boolean;
    createdBy: string;
    companyId: string;
  };
  url: string;
  totalPages: number;
  // True when the crawl hit the page ceiling, so the file holds part of the site
  // rather than all of it.
  truncated?: boolean;
  // True when the crawl was restricted to the website's sitemap, so a page the
  // site does not publish there is absent by design.
  sitemapOnly?: boolean;
}

export interface WebScraperErrorData {
  jobId: string;
  error: string;
  url: string;
}

export interface ScraperJob {
  jobId: string;
  companyId: string;
  userId: string;
  url: string;
  exclusions?: string[];
  status: WebScraperJobStatus;
  progress: number;
  total: number;
  truncated?: boolean;
  sitemapOnly?: boolean;
  error?: string;
  startedAt: Date;
  completedAt?: Date;
  dataSourceId?: string;
  scrapedData: ScrapedData[];
  dataSourceMetadata: DataSourceMetadata;
}

// The durable subset of a scraper job that is safe to persist. The scraped page
// markdown (`scrapedData`) is excluded because it grows per crawled page and would
// risk the 16MB BSON document limit - it stays in the in-process runtime registry.
export type PersistableScraperJob = Omit<ScraperJob, 'scrapedData'>;

export interface ScrapedData {
  markdown: string;
  metadata?: {
    sourceURL?: string;
    [key: string]: unknown;
  };
}

export interface DataSourceMetadata {
  label?: string;
  sharedWithCompanyIds?: string[];
  autoTraining?: boolean;
  autoInclude?: boolean;
  folderId?: string;
  expiryDate?: string;
  selectedLLMBlocks?: TLLMBlockSelection[];
}

export interface WebScraperProgressModalProps {
  jobId: string | null;
  url: string;
  exclusions?: string[];
  requestError?: string | null;
  onClose: () => void;
  onComplete: () => void;
}
